/* Copyright (c) 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { v4 } from "uuid";
import { Label, Select, Spacer, Text, theme } from "@nordcloud/gnui";
import { SystemActionsSortFields } from "~/generated/graphql";
import { isNotEmpty, sortItem } from "~/tools";
import { useGetSystemActions } from "~/views/plans/hooks/useGetSystemActions/useGetSystemActions";
import { PlanField } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";
import { SelectedPlanEntity } from "../../../types";
import { updateEntities } from "../utils";

type Option = {
  label: string;
  value: string;
  version: string;
  description?: string;
};

const ACTIONS_LIMIT = "100";

export function SelectActionVersion() {
  const { data } = useGetSystemActions({
    page: "1",
    limit: ACTIONS_LIMIT,
    sort: {
      field: SystemActionsSortFields.Name,
    },
  });

  const { stepState, updateStepState } = useStepState();

  const { setPlanData } = usePlanWizard();

  const actionWithVersions = data.filter(
    (item) => item.version && item.name === stepState.selectedEntity?.actionName
  );

  const sortActionBasedOnVersion = sortItem(
    actionWithVersions,
    (a, b) => parseFloat(a.version) - parseFloat(b.version)
  );

  const options = sortActionBasedOnVersion.map((i) => {
    return {
      label: `Version ${i.version}`,
      value: i?.id ?? "",
      version: i.version,
      description: i?.description ?? "",
    };
  });

  const currentVersion = stepState.selectedEntity?.version;
  const defaultValue = options.find((o) => o.version === currentVersion)?.value;
  const [value, setValue] = useState<string | undefined>(defaultValue);
  const hasCurrentVersion = isNotEmpty(currentVersion ?? "");

  useEffect(() => {
    const version = options.find((op) => op.version === currentVersion)?.value;
    setValue(version);
  }, [currentVersion, options]);

  const handleChangeVersion = (val?: string, selected?: SelectedPlanEntity) => {
    setValue(val);

    const action = data.find((i) => i.id === val);

    const actionWithId = {
      ...selected,
      ...action,
      listId: selected?.listId ?? v4(),
      actionName: action?.name,
    };

    const updatedEntities = updateEntities(stepState, actionWithId) ?? [];

    updateStepState({
      selectedEntity: actionWithId,
      entities: updatedEntities,
    });

    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        planEntities: updatedEntities,
      },
    }));
  };
  const formatOptionLabel = ({ label, description }: Option) => (
    <div>
      <Text tag="div">{label}</Text>
      <Text tag="div" size="sm" color={theme.color.text.text02}>
        {description}
      </Text>
    </div>
  );

  if (!hasCurrentVersion) {
    return null;
  }

  if (options.length <= 1) {
    return (
      <>
        <Label name="Action Version" />
        <Text data-testid="text-action-version">{`Version ${currentVersion}`}</Text>
        <Spacer height={theme.spacing.spacing04} />
      </>
    );
  }

  function withoutDescription(option?: Option): Omit<Option, "description"> {
    return {
      label: option?.label ?? "",
      value: option?.value ?? "",
      version: option?.version ?? "",
    };
  }

  return (
    <>
      <Label name="Action Version" />
      <Select
        isSearchable={false}
        name="Select"
        value={withoutDescription(options.find((i) => i.value === value))}
        onChange={(g: Option | null) =>
          handleChangeVersion(g?.value, stepState.selectedEntity)
        }
        options={options}
        formatOptionLabel={formatOptionLabel}
      />
      <Spacer height={theme.spacing.spacing04} />
    </>
  );
}
